import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Wallet, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, 
  PiggyBank, CreditCard, Smartphone, Building2, RefreshCw, Plus,
  AlertTriangle, History, Settings, ArrowRightLeft, Receipt, 
  DollarSign, Loader2, ChevronRight, Calendar, BarChart3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs et icônes des comptes
const ACCOUNT_CONFIG = {
  cash: { name: 'Caisse', icon: Wallet, color: 'emerald', bgColor: 'bg-emerald-50', textColor: 'text-emerald-600', borderColor: 'border-emerald-500' },
  bank: { name: 'Banque', icon: Building2, color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-600', borderColor: 'border-blue-500' },
  airtel_money: { name: 'Airtel Money', icon: Smartphone, color: 'red', bgColor: 'bg-red-50', textColor: 'text-red-600', borderColor: 'border-red-500' },
  moov_money: { name: 'Moov Money', icon: Smartphone, color: 'orange', bgColor: 'bg-orange-50', textColor: 'text-orange-600', borderColor: 'border-orange-500' }
};

const EXPENSE_CATEGORIES = {
  rent: 'Loyer',
  salaries: 'Salaires',
  electricity: 'Électricité',
  water: 'Eau',
  internet: 'Internet',
  maintenance: 'Entretien',
  taxes: 'Taxes',
  insurance: 'Assurance',
  marketing: 'Marketing',
  other: 'Autres'
};

// Composant carte de statistique
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'emerald', onClick }) => (
  <Card 
    className={`border-l-4 border-l-${color}-500 ${onClick ? 'cursor-pointer hover:bg-accent/50 transition-colors' : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-50`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
      {trend && (
        <div className={`flex items-center mt-2 text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
          {trend === 'up' ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
          {trendValue}
        </div>
      )}
    </CardContent>
  </Card>
);

// Composant carte de compte
const AccountCard = ({ account, config, onUpdate }) => {
  const Icon = config.icon;
  
  return (
    <div className={`p-4 rounded-lg border-2 ${config.borderColor} ${config.bgColor}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${config.textColor}`} />
          <span className="font-medium">{config.name}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onUpdate(account)}>
          <Settings className="w-4 h-4" />
        </Button>
      </div>
      <p className={`text-2xl font-bold ${config.textColor}`}>
        {account.balance?.toLocaleString('fr-FR')} F
      </p>
    </div>
  );
};

// Composant alerte financière
const AlertCard = ({ alert }) => {
  const severityColors = {
    critical: 'bg-red-50 border-red-500 text-red-700',
    warning: 'bg-orange-50 border-orange-500 text-orange-700',
    info: 'bg-blue-50 border-blue-500 text-blue-700'
  };
  
  return (
    <div className={`p-3 rounded-lg border-l-4 ${severityColors[alert.severity]}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium text-sm">{alert.title}</p>
          <p className="text-xs opacity-80">{alert.message}</p>
        </div>
      </div>
    </div>
  );
};

export default function TreasuryPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [historyPeriod, setHistoryPeriod] = useState('week');
  
  const [dashboard, setDashboard] = useState(null);
  const [cashPosition, setCashPosition] = useState(null);
  const [history, setHistory] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [expenses, setExpenses] = useState([]);
  
  // Modals
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  
  // Form states
  const [transferForm, setTransferForm] = useState({ from_account: '', to_account: '', amount: '', description: '' });
  const [transactionForm, setTransactionForm] = useState({ account_type: 'cash', amount: '', transaction_type: 'income', description: '' });
  const [expenseForm, setExpenseForm] = useState({ category: 'other', amount: '', description: '', account_type: 'cash' });
  const [recurringForm, setRecurringForm] = useState({ category: 'rent', name: '', amount: '', account_type: 'bank', day_of_month: 1 });
  const [newBalance, setNewBalance] = useState('');

  const fetchData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [dashboardRes, cashRes, alertsRes, recurringRes] = await Promise.all([
        fetch(`${API}/api/treasury/dashboard`, { headers }),
        fetch(`${API}/api/treasury/cash-position`, { headers }),
        fetch(`${API}/api/treasury/alerts`, { headers }),
        fetch(`${API}/api/treasury/recurring-expenses`, { headers })
      ]);
      
      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (cashRes.ok) setCashPosition(await cashRes.json());
      if (alertsRes.ok) setAlerts(await alertsRes.json());
      if (recurringRes.ok) setRecurringExpenses((await recurringRes.json()).recurring_expenses || []);
      
    } catch (error) {
      console.error('Error fetching treasury data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchHistory = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(
        `${API}/api/treasury/history?period=${historyPeriod}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        setHistory(await response.json());
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }, [token, historyPeriod]);

  const fetchExpenses = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(
        `${API}/api/treasury/expenses?days=30`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        setExpenses((await response.json()).expenses || []);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    } else if (activeTab === 'expenses') {
      fetchExpenses();
    }
  }, [activeTab, fetchHistory, fetchExpenses, historyPeriod]);

  // Handlers
  const handleTransfer = async () => {
    try {
      const response = await fetch(`${API}/api/treasury/transfer`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from_account: transferForm.from_account,
          to_account: transferForm.to_account,
          amount: parseFloat(transferForm.amount),
          description: transferForm.description
        })
      });
      
      if (response.ok) {
        toast.success('Transfert effectué');
        setShowTransferModal(false);
        setTransferForm({ from_account: '', to_account: '', amount: '', description: '' });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors du transfert');
      }
    } catch (error) {
      toast.error('Erreur lors du transfert');
    }
  };

  const handleTransaction = async () => {
    try {
      const response = await fetch(`${API}/api/treasury/transaction`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_type: transactionForm.account_type,
          amount: parseFloat(transactionForm.amount),
          transaction_type: transactionForm.transaction_type,
          description: transactionForm.description
        })
      });
      
      if (response.ok) {
        toast.success('Transaction enregistrée');
        setShowTransactionModal(false);
        setTransactionForm({ account_type: 'cash', amount: '', transaction_type: 'income', description: '' });
        fetchData();
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleExpense = async () => {
    try {
      const response = await fetch(`${API}/api/treasury/expenses`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category: expenseForm.category,
          amount: parseFloat(expenseForm.amount),
          description: expenseForm.description,
          account_type: expenseForm.account_type
        })
      });
      
      if (response.ok) {
        toast.success('Charge enregistrée');
        setShowExpenseModal(false);
        setExpenseForm({ category: 'other', amount: '', description: '', account_type: 'cash' });
        fetchData();
        fetchExpenses();
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleRecurringExpense = async () => {
    try {
      const response = await fetch(`${API}/api/treasury/recurring-expenses`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category: recurringForm.category,
          name: recurringForm.name,
          amount: parseFloat(recurringForm.amount),
          account_type: recurringForm.account_type,
          recurrence: 'monthly',
          day_of_month: parseInt(recurringForm.day_of_month),
          is_active: true
        })
      });
      
      if (response.ok) {
        toast.success('Charge récurrente créée');
        setShowRecurringModal(false);
        setRecurringForm({ category: 'rent', name: '', amount: '', account_type: 'bank', day_of_month: 1 });
        fetchData();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handlePayRecurring = async (recurringId) => {
    try {
      const response = await fetch(`${API}/api/treasury/recurring-expenses/${recurringId}/pay`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Charge payée');
        fetchData();
        fetchExpenses();
      } else {
        toast.error('Erreur lors du paiement');
      }
    } catch (error) {
      toast.error('Erreur lors du paiement');
    }
  };

  const handleUpdateBalance = async () => {
    if (!selectedAccount) return;
    
    try {
      const response = await fetch(
        `${API}/api/treasury/accounts/${selectedAccount.type}/balance?balance=${parseFloat(newBalance)}`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Solde mis à jour');
        setShowBalanceModal(false);
        setNewBalance('');
        setSelectedAccount(null);
        fetchData();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="treasury-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PiggyBank className="w-7 h-7 text-emerald-600" />
            Trésorerie Avancée
          </h1>
          <p className="text-muted-foreground">
            Vision complète de vos finances en temps réel
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowTransferModal(true)}>
            <ArrowRightLeft className="w-4 h-4 mr-2" />
            Transfert
          </Button>
          <Button size="sm" onClick={() => setShowTransactionModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Transaction
          </Button>
        </div>
      </div>

      {/* Cartes principales - Vue rapide en 3 secondes */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="CASH DISPONIBLE"
          value={`${dashboard?.cash_position?.total?.toLocaleString('fr-FR') || 0} F`}
          subtitle="Tous comptes confondus"
          icon={Wallet}
          color="emerald"
        />
        <StatCard
          title="CA DU JOUR"
          value={`${dashboard?.today?.revenue?.toLocaleString('fr-FR') || 0} F`}
          subtitle={`${dashboard?.today?.order_count || 0} commandes`}
          icon={TrendingUp}
          color="blue"
        />
        <StatCard
          title="DÉPENSES DU JOUR"
          value={`${dashboard?.today?.total_expenses?.toLocaleString('fr-FR') || 0} F`}
          subtitle="Achats + Charges"
          icon={TrendingDown}
          color="red"
        />
        <StatCard
          title="BÉNÉFICE NET"
          value={`${dashboard?.today?.net_profit?.toLocaleString('fr-FR') || 0} F`}
          subtitle={`Marge: ${dashboard?.today?.revenue > 0 ? Math.round((dashboard?.today?.net_profit / dashboard?.today?.revenue) * 100) : 0}%`}
          icon={DollarSign}
          color={dashboard?.today?.net_profit >= 0 ? 'emerald' : 'red'}
        />
      </div>

      {/* Alertes */}
      {alerts?.alert_count > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <span className="font-medium text-orange-800">{alerts.alert_count} alerte(s)</span>
            </div>
            <div className="space-y-2">
              {alerts.alerts?.slice(0, 3).map((alert) => (
                <AlertCard key={alert.id} alert={alert} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <Wallet className="w-4 h-4" />
            <span className="hidden sm:inline">Comptes</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">Historique</span>
          </TabsTrigger>
          <TabsTrigger value="expenses" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            <span className="hidden sm:inline">Charges</span>
          </TabsTrigger>
          <TabsTrigger value="analysis" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Analyse</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Comptes */}
        <TabsContent value="dashboard" className="space-y-4">
          {/* Comptes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Position de Trésorerie</CardTitle>
              <CardDescription>Solde par compte</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {cashPosition?.accounts?.map((account) => {
                  const config = ACCOUNT_CONFIG[account.type] || ACCOUNT_CONFIG.cash;
                  return (
                    <AccountCard 
                      key={account.type} 
                      account={account} 
                      config={config}
                      onUpdate={(acc) => {
                        setSelectedAccount(acc);
                        setNewBalance(acc.balance?.toString() || '0');
                        setShowBalanceModal(true);
                      }}
                    />
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Flux du jour */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowUpRight className="w-5 h-5 text-green-600" />
                  Argent Entrant
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <span>Ventes du jour</span>
                    <span className="font-bold text-green-600">
                      +{dashboard?.flows?.money_in?.toLocaleString('fr-FR') || 0} F
                    </span>
                  </div>
                  {dashboard?.payment_methods && Object.entries(dashboard.payment_methods).map(([method, amount]) => (
                    <div key={method} className="flex justify-between items-center text-sm pl-4">
                      <span className="text-muted-foreground capitalize">{method}</span>
                      <span>{amount?.toLocaleString('fr-FR')} F</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowDownRight className="w-5 h-5 text-red-600" />
                  Argent Sortant
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span>Total sorties</span>
                    <span className="font-bold text-red-600">
                      -{dashboard?.flows?.money_out?.toLocaleString('fr-FR') || 0} F
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm pl-4">
                    <span className="text-muted-foreground">Achats</span>
                    <span>{dashboard?.expenses_breakdown?.purchases?.toLocaleString('fr-FR') || 0} F</span>
                  </div>
                  <div className="flex justify-between items-center text-sm pl-4">
                    <span className="text-muted-foreground">Charges</span>
                    <span>{dashboard?.expenses_breakdown?.charges?.toLocaleString('fr-FR') || 0} F</span>
                  </div>
                  <div className="flex justify-between items-center text-sm pl-4">
                    <span className="text-muted-foreground">Décaissements</span>
                    <span>{dashboard?.expenses_breakdown?.disbursements?.toLocaleString('fr-FR') || 0} F</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Solde net */}
          <Card className={dashboard?.flows?.net_flow >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Solde net du jour</span>
                <span className={`text-2xl font-bold ${dashboard?.flows?.net_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {dashboard?.flows?.net_flow >= 0 ? '+' : ''}{dashboard?.flows?.net_flow?.toLocaleString('fr-FR') || 0} F
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Historique */}
        <TabsContent value="history" className="space-y-4">
          <div className="flex items-center justify-between">
            <Select value={historyPeriod} onValueChange={setHistoryPeriod}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Période" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">7 jours</SelectItem>
                <SelectItem value="month">30 jours</SelectItem>
                <SelectItem value="quarter">90 jours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Résumé période */}
          {history && (
            <div className="grid sm:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-muted-foreground">CA Total</p>
                  <p className="text-xl font-bold text-blue-600">
                    {history.summary?.total_revenue?.toLocaleString('fr-FR')} F
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-muted-foreground">Dépenses</p>
                  <p className="text-xl font-bold text-red-600">
                    {history.summary?.total_expenses?.toLocaleString('fr-FR')} F
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-muted-foreground">Bénéfice Net</p>
                  <p className={`text-xl font-bold ${history.summary?.total_net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {history.summary?.total_net_profit?.toLocaleString('fr-FR')} F
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-muted-foreground">Moyenne/jour</p>
                  <p className="text-xl font-bold">
                    {history.summary?.avg_daily_profit?.toLocaleString('fr-FR')} F
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Graphique simplifié */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Évolution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {history?.history?.map((day) => {
                  const maxRevenue = Math.max(...(history.history?.map(d => d.revenue) || [1]));
                  const revenuePercent = (day.revenue / maxRevenue) * 100;
                  
                  return (
                    <div key={day.date} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-20">
                        {new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}
                      </span>
                      <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${revenuePercent}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-24 text-right">
                        {day.revenue?.toLocaleString('fr-FR')} F
                      </span>
                      <span className={`text-xs w-20 text-right ${day.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {day.net_profit >= 0 ? '+' : ''}{day.net_profit?.toLocaleString('fr-FR')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Charges */}
        <TabsContent value="expenses" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">Charges & Dépenses</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowRecurringModal(true)}>
                <Calendar className="w-4 h-4 mr-2" />
                Récurrente
              </Button>
              <Button size="sm" onClick={() => setShowExpenseModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Charge
              </Button>
            </div>
          </div>

          {/* Charges récurrentes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Charges Récurrentes</CardTitle>
              <CardDescription>
                Total mensuel estimé: {recurringExpenses.reduce((sum, r) => sum + (r.is_active ? r.amount : 0), 0).toLocaleString('fr-FR')} F
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {recurringExpenses.length > 0 ? (
                  recurringExpenses.map((expense) => (
                    <div key={expense.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{expense.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {EXPENSE_CATEGORIES[expense.category] || expense.category} • Le {expense.day_of_month} du mois
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold">{expense.amount?.toLocaleString('fr-FR')} F</span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handlePayRecurring(expense.id)}
                        >
                          Payer
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    Aucune charge récurrente configurée
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Historique des charges */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Historique (30 jours)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {expenses.length > 0 ? (
                  expenses.slice(0, 20).map((expense) => (
                    <div key={expense.id} className="flex items-center justify-between p-2 hover:bg-accent rounded">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{EXPENSE_CATEGORIES[expense.category] || expense.category}</Badge>
                        <span className="text-sm">{expense.description}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-red-600">-{expense.amount?.toLocaleString('fr-FR')} F</p>
                        <p className="text-xs text-muted-foreground">{expense.date}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    Aucune charge enregistrée
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Analyse */}
        <TabsContent value="analysis" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Analyse de Rentabilité</CardTitle>
              <CardDescription>Basée sur les données du jour</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* CA */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Chiffre d'Affaires</span>
                    <span className="text-xl font-bold text-blue-600">
                      {dashboard?.today?.revenue?.toLocaleString('fr-FR')} F
                    </span>
                  </div>
                </div>

                {/* Coût matières */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span>- Coût matières (estimé)</span>
                    <span className="font-medium text-red-600">
                      {dashboard?.today?.material_cost?.toLocaleString('fr-FR')} F
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Ratio: {dashboard?.today?.revenue > 0 ? Math.round((dashboard?.today?.material_cost / dashboard?.today?.revenue) * 100) : 0}% du CA
                  </div>
                </div>

                {/* Bénéfice brut */}
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">= Bénéfice Brut</span>
                    <span className="text-xl font-bold text-emerald-600">
                      {dashboard?.today?.gross_profit?.toLocaleString('fr-FR')} F
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Marge brute: {dashboard?.today?.revenue > 0 ? Math.round((dashboard?.today?.gross_profit / dashboard?.today?.revenue) * 100) : 0}%
                  </div>
                </div>

                {/* Autres dépenses */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span>- Autres dépenses</span>
                    <span className="font-medium text-red-600">
                      {dashboard?.today?.total_expenses?.toLocaleString('fr-FR')} F
                    </span>
                  </div>
                </div>

                {/* Bénéfice net */}
                <div className={`p-4 rounded-lg ${dashboard?.today?.net_profit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  <div className="flex justify-between items-center">
                    <span className="font-bold">= BÉNÉFICE NET</span>
                    <span className={`text-2xl font-bold ${dashboard?.today?.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {dashboard?.today?.net_profit?.toLocaleString('fr-FR')} F
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Marge nette: {dashboard?.today?.revenue > 0 ? Math.round((dashboard?.today?.net_profit / dashboard?.today?.revenue) * 100) : 0}%
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal: Transfert */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfert entre comptes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>De</Label>
              <Select value={transferForm.from_account} onValueChange={(v) => setTransferForm({...transferForm, from_account: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Compte source" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ACCOUNT_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vers</Label>
              <Select value={transferForm.to_account} onValueChange={(v) => setTransferForm({...transferForm, to_account: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Compte destination" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ACCOUNT_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant (F CFA)</Label>
              <Input 
                type="number" 
                value={transferForm.amount} 
                onChange={(e) => setTransferForm({...transferForm, amount: e.target.value})}
                placeholder="0"
              />
            </div>
            <div>
              <Label>Description (optionnel)</Label>
              <Input 
                value={transferForm.description} 
                onChange={(e) => setTransferForm({...transferForm, description: e.target.value})}
                placeholder="Motif du transfert"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>Annuler</Button>
            <Button onClick={handleTransfer} disabled={!transferForm.from_account || !transferForm.to_account || !transferForm.amount}>
              Transférer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Transaction */}
      <Dialog open={showTransactionModal} onOpenChange={setShowTransactionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle transaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select value={transactionForm.transaction_type} onValueChange={(v) => setTransactionForm({...transactionForm, transaction_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Entrée d'argent</SelectItem>
                  <SelectItem value="expense">Sortie d'argent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Compte</Label>
              <Select value={transactionForm.account_type} onValueChange={(v) => setTransactionForm({...transactionForm, account_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ACCOUNT_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant (F CFA)</Label>
              <Input 
                type="number" 
                value={transactionForm.amount} 
                onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
                placeholder="0"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={transactionForm.description} 
                onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
                placeholder="Description de la transaction"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransactionModal(false)}>Annuler</Button>
            <Button onClick={handleTransaction} disabled={!transactionForm.amount || !transactionForm.description}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Charge */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle charge</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Catégorie</Label>
              <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({...expenseForm, category: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(EXPENSE_CATEGORIES).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Compte de paiement</Label>
              <Select value={expenseForm.account_type} onValueChange={(v) => setExpenseForm({...expenseForm, account_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ACCOUNT_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant (F CFA)</Label>
              <Input 
                type="number" 
                value={expenseForm.amount} 
                onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                placeholder="0"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={expenseForm.description} 
                onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                placeholder="Description de la charge"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpenseModal(false)}>Annuler</Button>
            <Button onClick={handleExpense} disabled={!expenseForm.amount || !expenseForm.description}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Charge récurrente */}
      <Dialog open={showRecurringModal} onOpenChange={setShowRecurringModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle charge récurrente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom</Label>
              <Input 
                value={recurringForm.name} 
                onChange={(e) => setRecurringForm({...recurringForm, name: e.target.value})}
                placeholder="Ex: Loyer local"
              />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={recurringForm.category} onValueChange={(v) => setRecurringForm({...recurringForm, category: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(EXPENSE_CATEGORIES).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant mensuel (F CFA)</Label>
              <Input 
                type="number" 
                value={recurringForm.amount} 
                onChange={(e) => setRecurringForm({...recurringForm, amount: e.target.value})}
                placeholder="0"
              />
            </div>
            <div>
              <Label>Jour du mois</Label>
              <Input 
                type="number" 
                min={1}
                max={28}
                value={recurringForm.day_of_month} 
                onChange={(e) => setRecurringForm({...recurringForm, day_of_month: e.target.value})}
              />
            </div>
            <div>
              <Label>Compte de paiement</Label>
              <Select value={recurringForm.account_type} onValueChange={(v) => setRecurringForm({...recurringForm, account_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ACCOUNT_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecurringModal(false)}>Annuler</Button>
            <Button onClick={handleRecurringExpense} disabled={!recurringForm.name || !recurringForm.amount}>
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Mise à jour solde */}
      <Dialog open={showBalanceModal} onOpenChange={setShowBalanceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajuster le solde - {selectedAccount && ACCOUNT_CONFIG[selectedAccount.type]?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nouveau solde (F CFA)</Label>
              <Input 
                type="number" 
                value={newBalance} 
                onChange={(e) => setNewBalance(e.target.value)}
                placeholder="0"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Solde actuel: {selectedAccount?.balance?.toLocaleString('fr-FR')} F
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBalanceModal(false)}>Annuler</Button>
            <Button onClick={handleUpdateBalance}>
              Mettre à jour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
